import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { MoreHorizontal, User } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';

import type { ListB2CUsers } from '@virtual-care/client';
import { displayName, formatPatientMid } from '@virtual-care/isomorphic';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@virtual-care/shadcn';
import { SearchInput, TableRowSort, client, useData } from '@virtual-care/ui';

import {
  ageColumn,
  emailColumn,
  genderColumn,
  phoneNumberColumn,
} from '../../components/columns.tsx';
import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import { ViewPatientDialog } from './view-patient';

export default function PatientsPage() {
  return (
    <div className="w-full">
      <PatientsTable />
    </div>
  );
}

function PatientsTable() {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewPatientId = searchParams.get('viewPatient');

  const viewPatientQuery = useData(
    'GET /patients/{id}',
    { id: viewPatientId! },
    { enabled: !!viewPatientId },
  );

  const handleCloseViewPatient = () => {
    setSearchParams((params) => {
      params.delete('viewPatient');
      return params;
    });
  };

  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const dataQuery = useQuery({
    queryKey: ['patients', pagination, sorting, searchQuery],
    queryFn: () => {
      return client.request('GET /patients/b2c', {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        query: searchQuery || undefined,
        sortBy: sorting.length > 0 ? sorting[0].id : undefined,
        sortOrder:
          sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : undefined,
      });
    },
    placeholderData: keepPreviousData,
  });

  const columns: ColumnDef<ListB2CUsers['records'][number]>[] = [
    {
      id: 'mid',
      header: 'MID',
      cell: ({ row }) => {
        return (
          <div className="font-mono">{formatPatientMid(row.original.mid)}</div>
        );
      },
    },
    {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => {
        return <div className="font-medium">{displayName(row.original)}</div>;
      },
    },
    emailColumn(),
    phoneNumberColumn(),
    genderColumn((original) => original.b2cProfile ?? undefined),
    ageColumn((original) => original.b2cProfile ?? undefined),
    {
      accessorKey: 'b2cProfile.nationalId',
      header: 'National ID',
      cell: ({ row }) => {
        const nationalId = row.original.b2cProfile?.nationalId;
        return (
          <div className="font-mono">
            {nationalId || <span className="text-muted-foreground">N/A</span>}
          </div>
        );
      },
    },
    {
      accessorKey: 'b2cProfile.residency',
      header: 'Residency',
      cell: ({ row }) => {
        const residency = row.original.b2cProfile?.residency;
        return (
          <div>
            {residency || <span className="text-muted-foreground">N/A</span>}
          </div>
        );
      },
    },
    {
      id: 'profileStatus',
      header: 'Profile Status',
      cell: ({ row }) => {
        return (
          <Badge
            variant={row.original.profileCompleted ? 'default' : 'secondary'}
          >
            {row.original.profileCompleted ? 'Complete' : 'Incomplete'}
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => {
        const patient = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() =>
                  navigator.clipboard.writeText(formatPatientMid(patient.mid))
                }
              >
                Copy MID ({formatPatientMid(patient.mid)})
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  navigator.clipboard.writeText(patient.phoneNumber || '')
                }
              >
                Copy phone number
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <ViewPatientDialog
                patient={patient}
                trigger={
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    View details
                  </DropdownMenuItem>
                }
              />
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data: dataQuery.data?.records ?? [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <>
      <CountablePageHeader
        title="Patients"
        totalCount={`${dataQuery.data?.totalCount} total patients`}
        icon={User}
      />

      <div className="mb-4">
        <SearchInput
          placeholder="Search patients by name, email, or phone number..."
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={() => setSearchQuery('')}
          isLoading={dataQuery.isFetching}
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </TableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading patients...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No patients found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />

      {viewPatientId && (
        <ViewPatientDialog
          patient={viewPatientQuery.data}
          isLoading={viewPatientQuery.isLoading}
          open={!!viewPatientId}
          onOpenChange={(open) => !open && handleCloseViewPatient()}
        />
      )}
    </>
  );
}
